import * as THREE from 'three';
import type { ParticleSystem } from '../fx/ParticleSystem';
import type { Terrain } from '../world/Terrain';
import { stepProjectile } from '@use/shared';
import type { Vec3, WeaponDef } from '@use/shared';

export interface ProjectileState {
  id: string;
  ownerId: string;
  weaponId: string;
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  age: number;
  active: boolean;
  bounces: number;
  weaponDef: WeaponDef;
}

export class ProjectileManager {
  private projectiles: Map<string, ProjectileState> = new Map();
  private meshes: Map<string, THREE.Mesh> = new Map();
  private scene: THREE.Scene;
  private terrain: Terrain;
  private particles: ParticleSystem;

  // Callbacks
  public onImpact?: (projectile: ProjectileState, position: THREE.Vector3) => void;

  constructor(scene: THREE.Scene, terrain: Terrain, particles: ParticleSystem) {
    this.scene = scene;
    this.terrain = terrain;
    this.particles = particles;
  }

  /**
   * Spawn a new projectile with weapon-specific visuals.
   */
  public spawn(
    id: string,
    ownerId: string,
    weaponDef: WeaponDef,
    position: Vec3,
    velocity: Vec3
  ): void {
    const state: ProjectileState = {
      id,
      ownerId,
      weaponId: weaponDef.id,
      position: new THREE.Vector3(position.x, position.y, position.z),
      velocity: new THREE.Vector3(velocity.x, velocity.y, velocity.z),
      age: 0,
      active: true,
      bounces: 0,
      weaponDef,
    };

    this.projectiles.set(id, state);

    // Create weapon-specific visual mesh
    const mesh = this.createProjectileMesh(weaponDef);
    mesh.position.copy(state.position);
    this.scene.add(mesh);
    this.meshes.set(id, mesh);
  }

  private createProjectileMesh(weaponDef: WeaponDef): THREE.Mesh {
    switch (weaponDef.id) {
      case 'single_shot': {
        const geo = new THREE.SphereGeometry(0.15, 6, 4);
        return new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0xffcc00 }));
      }
      case 'big_shot': {
        const geo = new THREE.SphereGeometry(0.25, 8, 6);
        return new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0xff8800 }));
      }
      case 'nuke': {
        const geo = new THREE.SphereGeometry(0.4, 10, 8);
        return new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0xff0000, emissive: new THREE.Color(0xff0000), emissiveIntensity: 0.5 } as any));
      }
      case 'sniper': {
        // Elongated cylinder for fast projectile look
        const geo = new THREE.CylinderGeometry(0.05, 0.05, 0.4, 6);
        geo.rotateX(Math.PI / 2);
        return new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0x00ccff }));
      }
      case 'shotgun': {
        const geo = new THREE.SphereGeometry(0.08, 4, 3);
        return new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0xffaa00 }));
      }
      case 'napalm': {
        const geo = new THREE.SphereGeometry(0.2, 8, 6);
        return new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0xff4400 }));
      }
      case 'roller': {
        const geo = new THREE.SphereGeometry(0.2, 8, 6);
        return new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0x88ff44 }));
      }
      case 'bouncer': {
        const geo = new THREE.SphereGeometry(0.15, 6, 4);
        return new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0xff44ff }));
      }
      case 'digger': {
        const geo = new THREE.SphereGeometry(0.2, 6, 4);
        return new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0x886633 }));
      }
      case 'dirt_mover': {
        const geo = new THREE.SphereGeometry(0.3, 8, 6);
        return new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0xcc9944 }));
      }
      case 'homing': {
        // Cone shape (rocket) — pointed forward
        const geo = new THREE.ConeGeometry(0.12, 0.35, 6);
        geo.rotateX(Math.PI / 2);
        return new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0xff2222 }));
      }
      case 'mirvs': {
        const geo = new THREE.SphereGeometry(0.2, 8, 6);
        return new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0xffff00 }));
      }
      case 'shockwave': {
        const geo = new THREE.SphereGeometry(0.3, 8, 6);
        return new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0x44aaff }));
      }
      case 'bazooka': {
        const geo = new THREE.SphereGeometry(0.3, 8, 6);
        return new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0xff6600 }));
      }
      default: {
        const geo = new THREE.SphereGeometry(0.15, 6, 4);
        return new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0xffcc00 }));
      }
    }
  }

  /**
   * Update all active projectiles.
   */
  public update(dt: number): void {
    for (const [id, proj] of this.projectiles) {
      if (!proj.active) continue;

      // Step physics
      const result = stepProjectile(
        { x: proj.position.x, y: proj.position.y, z: proj.position.z },
        { x: proj.velocity.x, y: proj.velocity.y, z: proj.velocity.z },
        proj.weaponDef.gravityMultiplier,
        dt
      );

      proj.position.set(result.position.x, result.position.y, result.position.z);
      proj.velocity.set(result.velocity.x, result.velocity.y, result.velocity.z);
      proj.age += dt;

      // Update visual mesh
      const mesh = this.meshes.get(id);
      if (mesh) {
        mesh.position.copy(proj.position);
      }

      // Emit weapon-specific trail particles
      this.emitWeaponTrail(proj);

      // Orient sniper and homing projectiles to face their velocity direction
      if ((proj.weaponDef.id === 'sniper' || proj.weaponDef.id === 'homing') && mesh) {
        const vel = proj.velocity.clone().normalize();
        mesh.lookAt(mesh.position.clone().add(vel));
      }

      // Check terrain collision
      const terrainH = this.terrain.getHeightAt(proj.position.x, proj.position.z);
      if (proj.position.y <= terrainH) {
        // Handle special behaviors
        if (proj.weaponDef.special === 'bouncing' && proj.bounces < 3) {
          // Bounce
          proj.bounces++;
          proj.position.y = terrainH + 0.1;
          proj.velocity.y = Math.abs(proj.velocity.y) * 0.6;

          // Magenta bounce sparks
          this.particles.emitBounceSparks(proj.position);
          this.onImpact?.(proj, proj.position.clone());
        } else if (proj.weaponDef.special === 'roller') {
          // Roll along terrain surface
          proj.position.y = terrainH + 0.2;
          proj.velocity.y = 0;

          // Get terrain slope to determine roll direction
          const normal = this.terrain.getNormalAt(proj.position.x, proj.position.z);
          proj.velocity.x += -normal.x * 15 * dt;
          proj.velocity.z += -normal.z * 15 * dt;

          // Friction
          proj.velocity.x *= 0.98;
          proj.velocity.z *= 0.98;

          // Stop if slow enough
          if (proj.velocity.lengthSq() < 0.5 || proj.age > 5) {
            this.impact(id, proj);
          }
        } else {
          // Normal impact
          proj.position.y = terrainH;
          this.impact(id, proj);
        }
      }

      // Max age timeout
      if (proj.age > 15) {
        this.remove(id);
      }
    }
  }

  private impact(id: string, proj: ProjectileState): void {
    proj.active = false;

    // Weapon-specific explosion visual
    this.emitWeaponExplosion(proj);

    // Callback for game logic (damage, terrain deform)
    this.onImpact?.(proj, proj.position.clone());

    // Remove mesh after a frame
    setTimeout(() => this.remove(id), 50);
  }

  private emitWeaponTrail(proj: ProjectileState): void {
    const pos = proj.position;
    switch (proj.weaponDef.id) {
      case 'bazooka':
        this.particles.emitBazookaTrail(pos);
        break;
      case 'nuke':
        this.particles.emitHeavySmokeTrail(pos);
        break;
      case 'napalm':
        this.particles.emitFireTrail(pos);
        break;
      case 'homing':
        this.particles.emitRocketTrail(pos);
        break;
      case 'sniper':
        this.particles.emitSparkTrail(pos, new THREE.Color(0.3, 0.8, 1));
        break;
      case 'shotgun':
        if (Math.random() < 0.3) this.particles.emitSparkTrail(pos, new THREE.Color(1, 0.7, 0.2));
        break;
      case 'bouncer':
        this.particles.emitTrail(pos, new THREE.Color(1, 0.3, 1));
        break;
      case 'roller':
        this.particles.emitDirtTrail(pos);
        break;
      case 'digger':
        this.particles.emitDirtTrail(pos);
        break;
      case 'dirt_mover':
        this.particles.emitTrail(pos, new THREE.Color(0.7, 0.6, 0.3));
        break;
      case 'mirvs':
        this.particles.emitSparkleTrail(pos);
        break;
      case 'shockwave':
        this.particles.emitSparkTrail(pos, new THREE.Color(0.3, 0.6, 1));
        break;
      case 'big_shot':
        this.particles.emitTrail(pos, new THREE.Color(1, 0.5, 0.15));
        break;
      default:
        if (proj.weaponDef.projectileSpeed > 0) {
          this.particles.emitTrail(pos);
        }
        break;
    }
  }

  private emitWeaponExplosion(proj: ProjectileState): void {
    const pos = proj.position;
    switch (proj.weaponDef.id) {
      case 'nuke':
        this.particles.emitNukeExplosion(pos, proj.weaponDef.blastRadius);
        break;
      case 'shockwave':
        this.particles.emitShockwaveExplosion(pos, proj.weaponDef.blastRadius);
        break;
      case 'napalm':
        // Small burst — the main effect is the fire zone
        this.particles.emitExplosion(pos, 2, 15);
        break;
      case 'bouncer':
        this.particles.emitExplosion(pos, proj.weaponDef.blastRadius, 30);
        this.particles.emitBounceSparks(pos);
        break;
      default:
        this.particles.emitExplosion(pos, proj.weaponDef.blastRadius);
        this.particles.emitDust(pos);
        break;
    }
  }

  public remove(id: string): void {
    this.projectiles.delete(id);
    const mesh = this.meshes.get(id);
    if (mesh) {
      this.scene.remove(mesh);
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
      this.meshes.delete(id);
    }
  }

  /**
   * Sync a projectile position from server (for remote projectiles).
   */
  public syncPosition(id: string, position: Vec3, velocity: Vec3): void {
    const proj = this.projectiles.get(id);
    if (proj) {
      proj.position.set(position.x, position.y, position.z);
      proj.velocity.set(velocity.x, velocity.y, velocity.z);
    }
  }

  public getProjectile(id: string): ProjectileState | undefined {
    return this.projectiles.get(id);
  }

  public getAll(): ProjectileState[] {
    return Array.from(this.projectiles.values());
  }

  public clear(): void {
    for (const [id] of this.projectiles) {
      this.remove(id);
    }
  }
}
